import { ElementRef, Injectable } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AssessmentTypeEnum } from '@haleo-frontend/data-access/enums';
import { Answer, Assessment, Client, Question } from '@haleo-frontend/data-access/models';
import {
    AssessmentService,
    ClientService,
    LanguageService,
    SleepDiaryService
} from '@haleo-frontend/data-access/services';
import { AbstractComponent, AppService, AssessmentUtils } from '@haleo-frontend/utils';
import { AnimationController, LoadingController } from '@ionic/angular';
import { Storage } from '@ionic/storage';
import { TranslateService } from '@ngx-translate/core';
import { interval, lastValueFrom } from 'rxjs';
import { takeUntil, takeWhile } from 'rxjs/operators';
import * as v4 from 'uuid/v4';

@Injectable()
export abstract class AssessmentAbstractComponent extends AbstractComponent {

    private sendingAnswer: boolean;
    protected questionCount: number;
    answersQueue: any[];
    client: Client;
    assessment: Assessment;
    question: Question | null;
    answer: Answer | undefined;
    enableNext: boolean;
    hideNext: boolean;
    loading: boolean;
    finished: boolean;
    progression: number;
    questionsHistory: string[];
    assessmentType: string;
    errorDetected: string | null;
    showError: boolean = false;
    date: string;
    locale: string;
    assessmentTypeSleepDiary = AssessmentTypeEnum.sleepDiary;
    isConditionalLast = false;

    constructor(protected assessmentService: AssessmentService,
                protected route: ActivatedRoute,
                protected router: Router,
                protected loadingCtrl: LoadingController,
                protected translateService: TranslateService,
                protected animationController: AnimationController,
                protected elementRef: ElementRef,
                protected languageService: LanguageService,
                protected sleepDiaryService: SleepDiaryService,
                protected clientService: ClientService,
                protected appService: AppService,
                protected storage: Storage) {
        super();
        this.answersQueue = [];
    }

    async onInit() {

        this.assessmentType = AssessmentTypeEnum.sleepDiary;
        this.enableNext = true;
        this.progression = 0;
        this.questionsHistory = [];

        this.assessment = await lastValueFrom(this.assessmentService.getAssessment(this.route.snapshot.params.assessment)
            .pipe(takeUntil(this.destroy)));

        this.assessmentType = this.assessment.type;
        this.questionCount = this.assessment.questions
            .filter((q: Question) => q.answerType !== 'calculated' && q.nextStep !== null && !q.skip && q.key.indexOf('.') < 0)
            .length;

        if (this.assessment.type === AssessmentTypeEnum.workingShift) {
            this.date = AssessmentUtils.getAnswerValue('WS_Q1', this.assessment.questions, this.assessment.answers);
        }

        this.clientService.get()
            .pipe(takeUntil(this.destroy))
            .subscribe(client => this.client = client);

        this.locale = this.languageService.locale;
    }

    fixedError(callback: any) {
        this.loading = true;

        this.assessmentService.getAssessment(this.route.snapshot.params.assessment)
            .pipe(takeUntil(this.destroy))
            .subscribe(assessment => {

                this.assessment = assessment;
                this.loading = false;
                this.enableNext = true;
                this.showError = false;
                this.errorDetected = null;
                callback();
            });
    }

    animationFadeIn(elementRef: any) {

        const animation = this.animationController.create()
            .addElement(elementRef.nativeElement)
            .duration(200)
            .fromTo('opacity', '0.1', '1');

        animation.play();
    }

    answered(value: any) {
        if (!this.loading && this.question) {

            const previousAnswerIndex = this.assessment.answers.findIndex(answer => answer.questionId == this.question?.id);

            let answer: any;
            if (previousAnswerIndex >= 0) {
                answer = this.assessment.answers[previousAnswerIndex];
                this.assessment.answers[previousAnswerIndex].value = value;
            } else {

                answer = {
                    uuid: v4(),
                    value,
                    questionId: this.question.id
                };

                this.assessment.answers.push(answer);
            }

            // validation
            if (!AssessmentUtils.validateTIB(this.assessment)) {
                this.errorDetected = 'timeInBed';
            } else if (!AssessmentUtils.validateNegativeTSTCheck(this.assessment)) {
                this.errorDetected = 'sleepEfficiency';
            } else {
                this.errorDetected = null;
            }

            this.sendAnswer(this.question, answer);
            this.enableNext = answer.value !== null || this.question.answerType === 'explanation';
        }
    }

    sendAnswer(question: Question, answer: Answer) {

        this.showError = false;
        this.answersQueue.push({
            question: question,
            answer
        });

        if (!this.sendingAnswer) {

            this.sendingAnswer = true;

            let asyncCall = false;

            this.route.queryParams
                .subscribe(params => {
                        asyncCall = !!params.async;
                    }
                );

            if (!this.appService.isSelfGuided() && (this.assessment.type.includes(AssessmentTypeEnum.postQ) || this.assessment.type.includes(AssessmentTypeEnum.sleepQ))) {
                asyncCall = true;
            } else if (this.appService.isSelfGuided() && this.assessment.type === AssessmentTypeEnum.sleepDiarySelfGuided) {
                asyncCall = false;
            }

            this.assessmentService.sendAnswer(this.assessment, question, answer, asyncCall, this.isConditionalLast)
                .subscribe(answerUpdated => {

                    const answerToUpdate = this.assessment.answers.findIndex(a => a.uuid == answer.uuid);
                    this.assessment.answers[answerToUpdate].uuid = answerUpdated.uuid;

                    const sent = this.answersQueue.findIndex((element: any) => element.question.key === question.key);
                    this.answersQueue.splice(sent, 1);
                    this.sendingAnswer = false;

                    if (this.answersQueue.length > 0) {

                        this.answersQueue.filter((element: any) => element.question.key === question.key)
                            .forEach((element: any) => element.answer.uuid = answerUpdated.uuid);

                        const toSend = this.answersQueue[0];
                        this.answersQueue.splice(0, 1);

                        this.sendAnswer(toSend.question, toSend.answer);
                    }
                });
        }
    }

    async terminate() {

        // Need to resend answer of the conditional question to complete the assessment
        if (this.isConditionalLast && this.question) {

            const answer = this.assessment.answers.find(answer => answer.questionId == this.question?.id);

            if (answer) {
                this.sendAnswer(this.question, answer);
            }
        }

        const appKey = this.appService.getAppKey();
        const message = this.translateService.instant('assessment.savingAnswers-' + appKey);

        const loading = await this.loadingCtrl.create({message: message});
        await loading.present();

        if (this.assessment.type.includes(AssessmentTypeEnum.sleepDiary)) {

            // For sleep diaries check if a postQ or ESS assessment needs to be done
            const postQs = await lastValueFrom(this.assessmentService.getPostQSatisfactionToComplete());

            if (postQs && postQs.length > 0) {

                loading.dismiss();
                this.router.navigate(['/assessment', postQs[0].uuid],
                    {
                        replaceUrl: true,
                        queryParams: {async: 1}
                    }
                );
                return;
            }

            const esss = await lastValueFrom(this.assessmentService.getESSToComplete());

            if (esss && esss.length > 0) {

                loading.dismiss();
                this.router.navigate(['/assessment', esss[0].uuid], {replaceUrl: true});
                return;
            }

            this.finished = true;

            this.returnToOrigin(loading);

        } else if (this.assessment.type === AssessmentTypeEnum.workingShift) {

            const sleepDiary = await lastValueFrom(this.sleepDiaryService.createSleepDiary(this.date, AssessmentTypeEnum.sleepDiaryShiftWork));

            if (sleepDiary && !sleepDiary.completedAt) {

                await this.router.navigate(['/assessment', sleepDiary.uuid], {replaceUrl: true});
                this.resetStates();
                loading.dismiss();

            } else {

                this.finished = true;
                this.returnToOrigin(loading);
            }

        } else {

            this.resetStates();
            this.finished = true;

            this.returnToOrigin(loading);
        }
    }

    returnToOrigin(loading: any) {
        if (this.answersQueue.length === 0) {
            loading.dismiss();
            this.completed(this.assessment.version);
        } else {
            const maxTimer = 2000 / 500; // 2 seconds in milliseconds
            let count = 0;
            const initialLength = this.answersQueue.length;

            // if length is not 0, keep looping until it is
            interval(500)
                .pipe(takeWhile(() => this.answersQueue.length > 0, true))
                .subscribe(() => {
                    if (count > maxTimer) {
                        loading.message = this.translateService.instant('assessment.apiCallTakingLonger')
                            + ' ' + (initialLength - this.answersQueue.length) + '/' + initialLength + ' ' + this.translateService.instant('assessment.answersProcessed');
                    }
                    if (this.answersQueue.length === 0) {
                        loading.dismiss();
                        this.completed(this.assessment.version);
                    }
                    count++;
                });
        }
    }

    resetStates() {
        this.questionsHistory = [];
        this.question = null;
        this.enableNext = true;
    }

    abstract completed(version: string): void;
}
